import React, { useEffect, useState, useCallback, ChangeEvent, useRef, useMemo } from 'react';
import { Combobox, Button, makeStyles, tokens } from '@fluentui/react-components';
import { SearchFilled } from '@fluentui/react-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { TSearchItem } from '../types';
import SearchMatches from './SearchMatches';
import { useSearchParams } from '@shared/hooks';
import { TItem, useFolder } from '@modules/items';
import { MAX_SEARCH_DROPDOWN_SIZE, UI_MARGIN_TOP } from '../constants';
import { useSearch } from '../api';
import { calculateRowsPerPage } from '@shared/helpers';

const useStyles = makeStyles({
  combo: {
    flex: 1,

    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${tokens.colorNeutralStroke2} !important`,
    boxShadow: tokens.shadow4,
    '&:hover': {
      border: `1px solid ${tokens.colorNeutralStroke1Hover} !important`,
    },
    '&:active, &:focus-within': {
      border: `1px solid ${tokens.colorNeutralStroke1Pressed} !important`,
      boxShadow: tokens.shadow8,
    },
    // On the white app frame beside the shortcuts rail a lighter shadow is enough.
    'body:has([data-shortcuts-rail]) &': {
      boxShadow: tokens.shadow2,
    },
    'body:has([data-shortcuts-rail]) &:focus-within': {
      boxShadow: tokens.shadow4,
    },

    // remove the blue line on focus
    '&::after': {
      display: 'none',
    },
  },
  showMoreButton: {
    width: '100%',
    height: '50px',
    padding: '0 16px',
    fontWeight: 400,
    color: tokens.colorBrandForegroundLink,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorBrandForegroundLinkHover,
    },
    '&:active, &:focus-within': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      color: tokens.colorBrandForegroundLinkPressed,
    },
  },
});

const SearchBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * If we are on the "search results" page:
   * - hide the search bar dropdown
   * - avoid navigating to the same page, replace the URL instead
   * - store `page` and `pageSize` in the URL
   */
  const isSearchResultsPage = location.pathname === '/search';

  // --- URL search params
  const { searchParams, change } = useSearchParams();
  const page = searchParams.get('page');
  const pageSize = searchParams.get('pageSize');
  const query = searchParams.get('query') || '';
  const folderId = searchParams.get('folderId') || undefined;

  // --- api calls
  const {
    data: searchData,
    error: searchError,
    isLoading: isLoadingSearch,
  } = useSearch({ query, pageSize: MAX_SEARCH_DROPDOWN_SIZE, folderId }, { isDisabled: isSearchResultsPage });
  const { data: folder, error: folderError, isLoading: isLoadingFolder } = useFolder();
  const error = useMemo(() => folderError || searchError, [folderError, searchError]);

  // --- combobox state and refs
  const [isOpen, setIsOpen] = useState(false);
  const queryInputRef = useRef<HTMLInputElement>(null);

  // --- items to display in the search bar
  const [items, setItems] = useState<(TSearchItem | TItem)[]>([]);
  useEffect(() => {
    if (error) {
      console.error('Error fetching search data:', error); // dev log
      setItems([]); // clear items on error
      return;
    }
    /**
     * Avoid setting data if:
     * - No folder data is available (should not happen)
     * - Folder data is still loading
     * - Search data is being loaded
     */
    if (!folder || isLoadingFolder || (!searchData && isLoadingSearch)) return;
    /**
     * Use either search data (if available) or folder data recent items (should always be available).
     * Limit results to `MAX_SEARCH_DROPDOWN_SIZE` for nice UX.
     */
    setItems(searchData ? searchData.items : folder.recentItems.slice(0, MAX_SEARCH_DROPDOWN_SIZE));
  }, [folder, isLoadingFolder, searchData, isLoadingSearch, error]);

  // --- handle submit (search icon-button click or Enter key)
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      // conditionally build URL params
      const params = new URLSearchParams();
      if (query) params.set('query', query);
      if (page) params.set('page', String(page));
      if (folderId) params.set('folderId', folderId);
      params.set('pageSize', pageSize ?? calculateRowsPerPage(UI_MARGIN_TOP));
      // navigate to the "search results" page or update the URL if already there
      navigate(`/search?${params.toString()}`, { replace: isSearchResultsPage });
      // close the dropdown and blur the input after a short delay to allow the URL change to take effect
      setTimeout(() => {
        if (queryInputRef.current) queryInputRef.current.blur();
        setIsOpen(false);
      }, 0);
    },
    [navigate, query, page, pageSize, folderId, isSearchResultsPage]
  );

  // --- combobox event handlers
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );
  const handleOnOpenChange = (e: any, data: { open: boolean }) => {
    if (e.target.tagName === 'INPUT' && e.type === 'click' && isOpen && !data.open) {
      // Ignore close if clicking inside the input while it's already open
      return;
    }
    setIsOpen(data.open);
    if (!data.open) {
      // close the dropdown and blur the input when it is closed
      if (queryInputRef.current) queryInputRef.current.blur();
    }
  };
  const handleOnInput = (e: ChangeEvent<HTMLInputElement>) => {
    change('query', e.target.value);
  };

  // --- add custom styles to the search dropdown. this is the simplest way to do it
  useEffect(() => {
    const listbox = document.querySelector('.fui-Combobox__listbox');
    if (isOpen && listbox) listbox.classList.add('!p-0', '!rounded-lg'); // custom styles
  }, [isOpen]);

  // --- handle opening the search bar with the '/' key
  // useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     const activeElement = document.activeElement as HTMLElement | null;
  //     const isTyping =
  //       activeElement &&
  //       (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable);

  //     if (!isTyping && e.key === '/') {
  //       e.preventDefault();
  //       setIsOpen(true);
  //       if (queryInputRef.current) {
  //         queryInputRef.current.focus();
  //         // Move cursor to the end of the input
  //         const input = queryInputRef.current;
  //         input.setSelectionRange(input.value.length, input.value.length);
  //       }
  //     }
  //   };

  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => {
  //     window.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, []);

  const classes = useStyles();

  return (
    <div className="relative w-full">
      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="flex items-center p-2">
        <Combobox
          placeholder="Search"
          size="medium"
          value={query}
          ref={queryInputRef}
          className={classes.combo}
          open={!isSearchResultsPage && isOpen}
          onOpenChange={handleOnOpenChange}
          onInput={handleOnInput}
          expandIcon={
            <Button
              icon={<SearchFilled className="transform rotate-90" />}
              onMouseDown={(e) => e.stopPropagation()} // prevent open/close on icon click
              appearance="transparent"
              title="Search"
              aria-label="Search"
              type="submit"
            />
          }
        >
          <div>
            {error && (
              <div className="text-red-500">
                <p>Error loading search results. Please try again or contact support.</p>
              </div>
            )}

            {items.length > 0 && (
              <div className="pb-2">
                <SearchMatches items={items} />
              </div>
            )}

            <div>
              <Button
                appearance="subtle"
                className={classes.showMoreButton}
                title="Show more results"
                aria-label="Show more results"
                onClick={handleSubmit}
              >
                Show more results
              </Button>
            </div>
          </div>
        </Combobox>
      </form>
    </div>
  );
};

export default SearchBar;
